import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "selectedValueInput", "selectedValueContainer", "resultTemplate"]
  static classes = [
    "resultsDefault", "resultsHidden", "resultsVisible"
  ]
  static values = {
    url: String,
    minLength: { type: Number, default: 3 },
    delay: { type: Number, default: 150 },
    queryParam: { type: String, default: "query" },
    hideEmptySelectedValue: { type: Boolean, default: true }
  }

  connect() {
    this.inputTarget.setAttribute("autocomplete", "off")
    this.inputTarget.setAttribute("spellcheck", "false")
    this.inputTarget.addEventListener("input", debounce(this.onInputChange, this.delayValue))
    this.inputTarget.addEventListener("keydown", this.onInputKeydown)
    this.inputTarget.addEventListener("blur", this.onInputBlur)

    this.resultsTarget.classList.add(this.resultsDefaultClass)
    this.resultsTarget.addEventListener("mousedown", this.onResultsMouseDown)
    this.resultsTarget.addEventListener("click", this.onResultsClick)

    this.closeResults()
    this.hideOrShowSelectedValueContainer()
  }

  disconnect() {
    this.inputTarget.removeEventListener("input", this.onInputChange)
    this.inputTarget.removeEventListener("keydown", this.onInputKeydown)
    this.inputTarget.removeEventListener("blur", this.onInputBlur)

    this.resultsTarget.removeEventListener("mousedown", this.onResultsMouseDown)
    this.resultsTarget.removeEventListener("click", this.onResultsClick)
  }

  /*
  **
  ** Event handling
  **
  */

  onInputChange = () => {
    this.clearSelectedValue()
    this.queryResults()
  }

  onInputBlur = (event) => {
    // Ignore blur if mouse is down on results. Otherwise we will not be able to select a result with a mouse click.
    if (this.mouseDown) return

    this.closeResults()
  }

  onInputKeydown = (event) => {
    const handler = this[`onInput${event.key}Keydown`]
    if (handler) handler(event)
  }

  onInputEscapeKeydown = (event) => {
    event.preventDefault()
    this.closeResults()
  }

  onInputArrowDownKeydown = (event) => {
    event.preventDefault()

    const resultNode = this.nextResultNode({direction: "next"})
    if (resultNode) this.selectResult(resultNode)
  }

  onInputArrowUpKeydown = (event) => {
    event.preventDefault()

    const resultNode = this.nextResultNode({direction: "previous"})
    if (resultNode) this.selectResult(resultNode)
  }

  onInputEnterKeydown = (event) => {
    event.preventDefault()

    if (this.resultsVisible) {
      const selectedResultNode = this.selectedResultNode
      if (selectedResultNode) {
        this.commitSelectedResult(selectedResultNode)
      } else {
        this.closeResults()
      }
    } else {
      this.queryResults()
    }
  }

  onResultsMouseDown = () => {
    this.mouseDown = true
    this.resultsTarget.addEventListener("mouseup", () => {
      this.mouseDown = false
      this.inputTarget.focus()
    }, { once: true })
  }

  onResultsClick = (event) => {
    event.preventDefault()

    if (!(event.target instanceof Element)) return

    const selectedResultNode = event.target.closest("[data-autocomplete-value]")
    if (selectedResultNode) {
      this.commitSelectedResult(selectedResultNode)
    } else {
      this.closeResults()
    }
  }

  /*
  **
  ** Results UI handling
  **
  */

  queryResults() {
    const query = this.inputTarget.value.trim()
    if (query && query.length >= this.minLengthValue) {
      this.fetchResults(query).then((results) => {
        if (results && Array.isArray(results) && results.length > 0) {
          this.replaceResults(results)
          this.openResults()
        } else {
          this.closeResults()
        }
      })
    } else {
      this.closeResults()
    }
  }

  closeResults = ({clear = true} = {}) => {
    this.resultsTarget.classList.remove(this.resultsVisibleClass)
    this.resultsTarget.classList.add(this.resultsHiddenClass)
    this.resultsTarget.hidden = true

    if (clear) {
      this.replaceResults([])
    }
  }

  openResults() {
    this.resultsTarget.classList.remove(this.resultsHiddenClass)
    this.resultsTarget.classList.add(this.resultsVisibleClass)
    this.resultsTarget.hidden = false
  }

  get resultsVisible() {
    return !this.resultsTarget.hidden
  }

  get resultsHidden() {
    return !this.resultsVisible
  }

  replaceResults(results) {
    this.resultsTarget.innerHTML = ""

    if (Array.isArray(results) && results.length > 0) {
      results.forEach(result => {
        const resultNode = this.resultTemplateTarget.content.cloneNode(true).firstElementChild
        resultNode.textContent = result.label
        resultNode.dataset.autocompleteValue = result.value

        this.resultsTarget.appendChild(resultNode)
      })
    }
  }

  hideOrShowSelectedValueContainer() {
    if (!this.hideEmptySelectedValueValue) return
    if (!this.hasSelectedValueInputTarget) return
    if (!this.hasSelectedValueContainerTarget) return

    const value = this.selectedValueInputTarget.value
    this.selectedValueContainerTarget.hidden = !value
  }

  /*
  **
  ** Results selection handling
  **
  */

  nextResultNode({direction="next"} = {}) {
    const resultNodes = this.resultNodes
    const selectedResultNode = this.selectedResultNode
    const index = resultNodes.indexOf(selectedResultNode)
    const sibling = direction === "next" ? resultNodes[index + 1] : resultNodes[index - 1]
    const def = direction === "next" ? resultNodes[0] : resultNodes[resultNodes.length - 1]
    return sibling || def
  }

  get resultNodes() {
    return Array.from(this.resultsTarget.querySelectorAll("[data-autocomplete-value]"))
  }

  get selectedResultNode() {
    return this.resultsTarget.querySelector("[data-autocomplete-value].active")
  }

  selectResult(resultNode) {
    const previouslySelected = this.selectedResultNode
    if (previouslySelected) {
      previouslySelected.classList.remove("active")
      previouslySelected.removeAttribute("aria-current")
    }

    resultNode.classList.add("active")
    resultNode.setAttribute("aria-current", "true")
    resultNode.scrollIntoView({ behavior: "auto", block: "nearest" })
  }

  commitSelectedResult(selectedResultNode) {
    if (!selectedResultNode) return

    const textValue = selectedResultNode.textContent.trim()
    const value = selectedResultNode.dataset.autocompleteValue || textValue

    this.selectedValue = value

    this.inputTarget.value = textValue
    this.inputTarget.focus()
    this.element.scrollIntoView({ behavior: "auto", block: "nearest" })
    this.closeResults()
  }

  clearSelectedValue() {
    this.selectedValue = ""
  }

  set selectedValue(value) {
    if (this.hasSelectedValueInputTarget) {
      this.selectedValueInputTarget.value = value
      this.hideOrShowSelectedValueContainer()
    }
  }

  /*
  **
  ** Network tasks
  **
  */

  fetchResults = async (query) => {
    if (!this.hasUrlValue) return

    const url = this.buildURL(query)
    const data = await this.doFetch(url)

    return data
  }

  buildURL(query) {
    const url = new URL(this.urlValue, window.location.href)
    const params = new URLSearchParams(url.search.slice(1))
    params.append(this.queryParamValue, query)
    url.search = params.toString()

    return url.toString()
  }

  doFetch = async (url) => {
    const response = await fetch(url)

    if (!response.ok) {
      throw new Error(`Server request failed: ${response.statusText}`)
    }

    const data = await response.json()
    return data
  }
}

const debounce = (fn, delay = 10) => {
    let timeoutId = null

    return (...args) => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(fn, delay)
    }
  }
